import React from "react"
import { Box, ButtonBase } from "@mui/material"
import { NumberFieldSeeker, SelectSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"
import { AppFormGrid, AppFormSection, AppNote, AppPillToggle } from "@components"
import { LIMITS, WHOLE_MARKET, clampRank, compsetOption, selectTier } from "./@services/profile"
import { Cell, FieldLabel, Helper, NOT_SET, fromPill } from "./fields"


const RANK_PILLS = [{ value: NOT_SET, label: 'Not set' }, ...[1, 2, 3, 4, 5].map(n => ({ value: n, label: `#${n}` }))]

/**
 * "Who you compare against, and where you want to be": a tier or the whole market (C3) and the target
 * rank. A broker's identity is a site: its compset of sites is not built yet, so the hole is said
 * instead of a selector (criterion 4b).
 */
export default function CompsetSection({ value, options, axis, onPatch, onChange, onOpenTiers, disabled }) {
	const tiers = options?.tiers || []
	const rank = value.target_rank
	const tiersLink = <ButtonBase onClick={onOpenTiers} sx={{
		px: 0.25, fontSize: 'inherit', fontWeight: 600, fontFamily: 'inherit', color: dsColorNew.appBlue, verticalAlign: 'baseline',
	}}>Tiers</ButtonBase>

	return <AppFormSection
		icon="bullseye"
		title="Who you compare against, and where you want to be"
		hint="Your compset and your target position — what turns “you are above market” into “cut to €71.90 and you are #2”."
	>
		<AppFormGrid>
			{axis === 'site'
				? <Cell span={12}>
					<AppNote severity="info"><b>Coming with the broker role.</b> Your identity is a site, so your compset will be other sites — that selector is not built yet. Everything else on this page works today.</AppNote>
				</Cell>
				: <Cell span={4}>
					<SelectSeeker
						label="Compset"
						disabled={disabled}
						value={compsetOption(value.compset)}
						options={[{ value: WHOLE_MARKET, label: 'Whole market' }, ...tiers.map(t => ({ value: t, label: t }))]}
						onChange={e => onChange(selectTier(value, e.target.value))}
					/>
					<Helper>{tiers.length
						? <>One of <b>your tiers</b> ({tiers.join(' · ')}) or the whole market. Define tiers in Settings › {tiersLink}.</>
						: <>You have no tiers yet, so Iris compares you against the whole market. Define tiers in Settings › {tiersLink}.</>}</Helper>
				</Cell>}
			<Cell span={axis === 'site' ? 12 : 8}>
				<FieldLabel note={`#1–#${LIMITS.rank} in your compset`}>Target rank</FieldLabel>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
					<AppPillToggle
						size="small" aria-label="Target rank"
						value={rank === null ? NOT_SET : rank}
						options={RANK_PILLS}
						onChange={v => !disabled && onPatch({ target_rank: fromPill(v) })}
					/>
					<NumberFieldSeeker
						disabled={disabled}
						value={rank ?? ''}
						onChange={e => onPatch({ target_rank: clampRank(e.target.value) })}
						inputProps={{ min: 1, max: LIMITS.rank, 'aria-label': 'Target rank, 1 to 20' }}
						placeholder="…"
						sx={{ width: 88 }}
						fullWidth={false}
					/>
					<Helper sx={{ mt: 0 }}>or type one up to #{LIMITS.rank} · empty = no target</Helper>
				</Box>
			</Cell>
		</AppFormGrid>
	</AppFormSection>
}
